/** @format */
import React, { FunctionComponent } from 'react';
import { SimCardServiceTypeItem } from '../../../../interfaces/collections/SimCardServiceInterface';
import Card from '../../../../components/Card/Card';
import CardBody from '../../../../components/Card/CardBody';
import CardHeader from '../../../../components/Heading/CardHeaderExt';
import { Build } from '@material-ui/icons';
import GridContainer from '../../../../components/Grid/GridContainer';
import GridItem from '../../../../components/Grid/GridItem';
import I18n from '../../../../components/Translate/I18n';
import Typography from '@material-ui/core/Typography';
import { BoolTranslateHelper } from '../../../../helpers/BoolTranslateHelper';
import { OperatorMvneType, ServicesMvneType, useOperatorType, useServiceUseType } from '../../../../modules/lib/hooks/useOperatorType';
import { FeatureTypes } from '../../../../modules/features/FeaturesTypes';
import { useSimCardServiceGroup, useSimCardServiceGroupList } from '../../../../modules/lib/hooks/useSimCardServiceGroupList';
import CustomTextButtom from '../../../../components/Buttons/CustomTextButton';
import { roles, UserRoleHelper } from '../../../../helpers/UserRoleHelper';
import { useDispatch } from 'react-redux';
import { getServiceUseTypeName } from '../../../../modules/features/serviceUseTypes';

type OplSimCardServiceTypeInfoBoxSdgType = {
    simCardServiceType: SimCardServiceTypeItem;
    title: string;
    openEdit: () => void;
};

export const OplSimCardServiceTypeInfoBox: FunctionComponent<OplSimCardServiceTypeInfoBoxSdgType> = ({
    simCardServiceType,
    title,
    openEdit,
}): React.ReactElement => {
    const dispatch = useDispatch();
    const featureOperatorType = new FeatureTypes(process.env);
    const operatorType = useOperatorType(featureOperatorType);
    const serviceUseTypes = useServiceUseType();
    const groupCollection = useSimCardServiceGroupList(featureOperatorType.operatorType, true);
    const groupItem = useSimCardServiceGroup(groupCollection, simCardServiceType.groupId);
    const getOperatorName = (operatorId: number | null): string => {
        for (let i = 0; i < operatorType.length; i++) {
            if (operatorType[i].value === operatorId) {
                return operatorType[i].name;
            }
        }
        return OperatorMvneType.PLUS;
    };

    const cdrOnAmountConvert = (value: number): string => {
        let translate = 'general:common.no';
        if (value === 1) {
            translate = 'general:common.yes';
        }
        return translate;
    };

    return (
        <Card>
            <CardHeader icon={<Build />} trans={title} transValueObject={{ service_name: simCardServiceType.description }} />
            <CardBody>
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {simCardServiceType.serviceType !== ServicesMvneType.BME && <I18n trans="prov:feature.component_id" />}
                            {simCardServiceType.serviceType === ServicesMvneType.BME && <I18n trans="prov:feature.bme_component_id" />}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>{simCardServiceType.mainname}</GridItem>
                    {simCardServiceType.serviceType === ServicesMvneType.BME && (
                        <React.Fragment>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="prov:feature.external_id" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>{simCardServiceType.externalSystemId}</GridItem>
                        </React.Fragment>
                    )}
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="prov:feature.group_name" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>{groupItem?.mainname}</GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="prov:feature.operator_mvne" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>{getOperatorName(simCardServiceType.operatorType)}</GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="prov:feature.visible" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <I18n trans={BoolTranslateHelper(simCardServiceType.visibleFlag)} />
                    </GridItem>
                    {simCardServiceType.serviceType === ServicesMvneType.BME && (
                        <React.Fragment>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="prov:feature.sync_usage" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <I18n trans={BoolTranslateHelper(simCardServiceType.syncUsage)} />
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="prov:feature.sync_detail" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <I18n trans={BoolTranslateHelper(simCardServiceType.syncDetails)} />
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="prov:feature.single_shot" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <I18n trans={BoolTranslateHelper(simCardServiceType.singleShotFlag)} />
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="prov:feature.cycle_component" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <I18n trans={simCardServiceType.cycle === -1 ? 'general:common.no' : 'general:common.yes'} />
                            </GridItem>
                        </React.Fragment>
                    )}
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="prov:feature.comment" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>{simCardServiceType.comment}</GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="prov:feature.service_type" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>{simCardServiceType.serviceType}</GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="prov:feature.use_type" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>{getServiceUseTypeName(simCardServiceType.useType, serviceUseTypes)}</GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="prov:feature.generate_cdr" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <I18n trans={BoolTranslateHelper(simCardServiceType.generateCdr)} />
                    </GridItem>



                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="prov:feature.allow_waiting_activation" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <I18n trans={BoolTranslateHelper(simCardServiceType.allowWaitingForActivation)} />
                    </GridItem>

                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="prov:feature.allow_mcc" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <I18n trans={BoolTranslateHelper(simCardServiceType.mccCheck)} />
                    </GridItem>


                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="prov:feature.allow_zone" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <I18n trans={BoolTranslateHelper(simCardServiceType.zoneCheck)} />
                    </GridItem>



                    {simCardServiceType.serviceType === ServicesMvneType.BME && (
                        <>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="prov:feature.generate_cdr_with_billsec_equal_amount" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <I18n trans={cdrOnAmountConvert(simCardServiceType.cdrOnAmount)} />
                            </GridItem>
                        </>
                    )}
                    {UserRoleHelper.checkRole(roles.ROLE_SIM_CARD_SERVICE_TYPE_PUT) && (
                        <GridItem xs={12} style={{ textAlign: 'right' }}>
                            <CustomTextButtom callback={openEdit} label="general:common.edit" size="sm" />
                        </GridItem>
                    )}
                </GridContainer>
            </CardBody>
        </Card>
    );
};
